// frontend/src/charts/METRICS.ts
import { formatRub, formatPct, formatInt } from "./format.js";

export type MetricCategory = "money" | "funnel" | "quality";

export interface MetricMeta {
  id: string;
  label: string;
  category: MetricCategory;
  seriesColor: string;        // CSS var name, e.g. "var(--series-1)"
  format: (n: number | null) => string;
  yAxisDomain?: [number | "auto", number | "auto"];
  invertGoodSign?: boolean;   // true для cost / bounce_rate (рост = плохо)
  description?: string;       // короткое описание для tooltip
  formula?: string;           // SQL/мат-формула для tooltip
}

export const METRICS = {
  cost_rub: {
    id: "cost_rub",
    label: "Расход",
    category: "money",
    seriesColor: "var(--series-6)",
    format: formatRub,
    invertGoodSign: true,
    description: "Сумма потраченного на рекламу за период.",
    formula: "SUM(cost_micros) / 1000000",
  },
  gross_revenue: {
    id: "gross_revenue",
    label: "Заказы, ₽",
    category: "money",
    seriesColor: "var(--series-2)",
    format: formatRub,
    description: "Сумма всех оформленных заказов (намерение). По канону Service v3 это «Заказы, ₽», НЕ выручка (выручка = нетто-выкуп).",
    formula: "SUM(total_final_price) всех заказов КИТ",
  },
  net_roas: {
    id: "net_roas",
    label: "net-ROAS",
    category: "money",
    seriesColor: "var(--series-1)",
    format: (n: number | null) => formatPct(n, 0),
    description: "Окупаемость рекламы по чистой выручке (после комиссии ВБ и возвратов).",
    formula: "net_revenue / cost_rub × 100%",
  },
  impressions: {
    id: "impressions",
    label: "Показы",
    category: "funnel",
    seriesColor: "var(--series-1)",
    format: formatInt,
    description: "Сколько раз рекламное объявление было показано пользователям.",
    formula: "SUM(impressions)",
  },
  clicks: {
    id: "clicks",
    label: "Клики",
    category: "funnel",
    seriesColor: "var(--series-3)",
    format: formatInt,
    description: "Сколько раз пользователи кликнули по рекламе.",
    formula: "SUM(clicks)",
  },
  visits: {
    id: "visits",
    label: "Посетители",
    category: "funnel",
    seriesColor: "var(--series-2)",
    format: formatInt,
    description: "Сколько визитов на сайт пришло с рекламы (визит ≠ клик из-за bounce и потерь).",
    formula: "COUNT(DISTINCT visit_id) WHERE source = 'ads'",
  },
  purchases: {
    id: "purchases",
    label: "Покупки",
    category: "funnel",
    seriesColor: "var(--series-5)",
    format: formatInt,
    description: "Количество совершённых покупок с рекламного трафика.",
    formula: "COUNT(orders) WHERE source = 'ads'",
  },
  ctr: {
    id: "ctr",
    label: "CTR",
    category: "quality",
    seriesColor: "var(--series-3)",
    format: (n: number | null) => formatPct(n, 2),
    description: "Кликабельность объявления — доля показов, перешедших в клик.",
    formula: "clicks / impressions × 100%",
  },
  cr_visit_to_purchase: {
    id: "cr_visit_to_purchase",
    label: "CR (визит → заказ)",
    category: "quality",
    seriesColor: "var(--series-5)",
    format: (n: number | null) => formatPct(n, 2),
    description: "Доля визитов с рекламы, завершившихся покупкой.",
    formula: "purchases / visits × 100%",
  },
  aov: {
    id: "aov",
    label: "Чек заказа",
    category: "quality",
    seriesColor: "var(--series-2)",
    format: formatRub,
    description: "Средний чек заказа (по канону Service v3 — одна из двух метрик «средний чек», рядом с «Чек выкупа»).",
    formula: "Заказы ₽ / заказы шт",
  },
  bounce_rate: {
    id: "bounce_rate",
    label: "Bounce rate",
    category: "quality",
    seriesColor: "var(--series-6)",
    format: (n: number | null) => formatPct(n, 1),
    invertGoodSign: true,
    description: "Доля визитов с одной просмотренной страницей (отказы).",
    formula: "AVG(bounce::int) × 100%",
  },

  // === Money — 3 новых ===
  net_revenue: { id: "net_revenue", label: "Выручка (выкуп)", category: "money",
    seriesColor: "var(--series-2)", format: formatRub,
    description: "Выручка = нетто-выкуп (выкуплено − возвраты), по канону Service v3 — главная метрика «Выручка».",
    formula: "SUM(purchased_price) выкупленных заказов КИТ" },
  roas: { id: "roas", label: "ROAS (по заказам)", category: "money",
    seriesColor: "var(--series-1)", format: (n: number | null) => formatPct(n, 0),
    description: "Окупаемость рекламы по сумме заказов (gross). Каноничный показатель — net-ROAS (по выручке-выкупу).",
    formula: "Заказы ₽ / cost_rub × 100%" },
  true_aov: { id: "true_aov", label: "Чек выкупа", category: "money",
    seriesColor: "var(--series-2)", format: formatRub,
    description: "Средний чек выкупа (по канону Service v3 — вторая метрика «средний чек», рядом с «Чек заказа»).",
    formula: "Выручка (выкуп) / выкупы шт" },

  // === Funnel reach — 22 цели Я.Метрики ===
  // Основная воронка покупки (8 канонических)
  reach_product_list_view: { id: "reach_product_list_view", label: "Просмотр списка",
    category: "funnel", seriesColor: "var(--series-1)", format: formatInt,
    description: "Сработало достижений цели «Просмотр списка товаров» в Я.Метрике.",
    formula: "SUM(reaches) WHERE goal_id = product_list_view" },
  reach_product_view: { id: "reach_product_view", label: "Просмотр товара",
    category: "funnel", seriesColor: "var(--series-1)", format: formatInt,
    description: "Сколько раз посмотрели карточку товара.",
    formula: "SUM(reaches) WHERE goal_id = product_view" },
  reach_product_features_view: { id: "reach_product_features_view", label: "Просмотр характеристик",
    category: "funnel", seriesColor: "var(--series-1)", format: formatInt,
    description: "Сколько раз раскрыли блок характеристик товара.",
    formula: "SUM(reaches) WHERE goal_id = product_features_view" },
  reach_product_features_change: { id: "reach_product_features_change", label: "Изменение характеристики",
    category: "funnel", seriesColor: "var(--series-3)", format: formatInt,
    description: "Сколько раз пользователь сменил размер/цвет/вариант товара.",
    formula: "SUM(reaches) WHERE goal_id = product_features_change" },
  reach_product_click: { id: "reach_product_click", label: "Клик по товару",
    category: "funnel", seriesColor: "var(--series-3)", format: formatInt,
    description: "Сколько раз кликнули по карточке товара в листинге.",
    formula: "SUM(reaches) WHERE goal_id = product_click" },
  reach_basket: { id: "reach_basket", label: "В корзину",
    category: "funnel", seriesColor: "var(--series-3)", format: formatInt,
    description: "Сколько раз сработала цель «Добавить в корзину» — ранний прокси покупки.",
    formula: "SUM(reaches) WHERE goal_id = 491043521" },
  reach_basket_remove: { id: "reach_basket_remove", label: "Удалить из корзины",
    category: "funnel", seriesColor: "var(--series-6)", format: formatInt, invertGoodSign: true,
    description: "Сколько раз удалили товар из корзины — сигнал сомнения.",
    formula: "SUM(reaches) WHERE goal_id = basket_remove" },
  reach_checkout: { id: "reach_checkout", label: "Чекаут",
    category: "funnel", seriesColor: "var(--series-4)", format: formatInt,
    description: "Сколько раз пользователь начал оформлять заказ.",
    formula: "SUM(reaches) WHERE goal_id = checkout" },
  reach_order_placed: { id: "reach_order_placed", label: "Оформлено",
    category: "funnel", seriesColor: "var(--series-5)", format: formatInt,
    description: "Сколько заказов оформлено (любой тип оплаты).",
    formula: "SUM(reaches) WHERE goal_id = order_placed" },
  reach_order_prepaid: { id: "reach_order_prepaid", label: "Заказ предоплата",
    category: "funnel", seriesColor: "var(--series-5)", format: formatInt,
    description: "Заказы оформлены с предоплатой картой.",
    formula: "SUM(reaches) WHERE goal_id = order_prepaid" },
  reach_order_postpaid: { id: "reach_order_postpaid", label: "Заказ постоплата",
    category: "funnel", seriesColor: "var(--series-5)", format: formatInt,
    description: "Заказы оформлены с постоплатой при получении.",
    formula: "SUM(reaches) WHERE goal_id = order_postpaid" },
  reach_order_delivered: { id: "reach_order_delivered", label: "Доставлено",
    category: "funnel", seriesColor: "var(--series-5)", format: formatInt,
    description: "Заказы со статусом «Доставлено» (выкуплено покупателем).",
    formula: "SUM(reaches) WHERE goal_id = order_delivered" },
  reach_order_cancelled: { id: "reach_order_cancelled", label: "Отменено",
    category: "funnel", seriesColor: "var(--series-6)", format: formatInt, invertGoodSign: true,
    description: "Заказы отменены пользователем или ВБ.",
    formula: "SUM(reaches) WHERE goal_id = order_cancelled" },
  // Ecommerce-дубли (валидаторы)
  reach_ecom_purchase: { id: "reach_ecom_purchase", label: "Ecom: покупка",
    category: "funnel", seriesColor: "var(--series-5)", format: formatInt,
    description: "Покупки по Ecommerce-данным Я.Метрики (валидатор).",
    formula: "SUM(reaches) WHERE goal_type = ecommerce_purchase" },
  reach_ecom_basket: { id: "reach_ecom_basket", label: "Ecom: в корзину",
    category: "funnel", seriesColor: "var(--series-3)", format: formatInt,
    description: "Добавления в корзину по Ecommerce-данным (валидатор).",
    formula: "SUM(reaches) WHERE goal_type = ecommerce_basket" },
  // Контактные данные (формы / соцсети)
  reach_contact_provided: { id: "reach_contact_provided", label: "Контакты предоставлены",
    category: "funnel", seriesColor: "var(--series-4)", format: formatInt,
    description: "Пользователь оставил контакты (email / телефон) на сайте.",
    formula: "SUM(reaches) WHERE goal_id = contact_provided" },
  reach_contact_sent: { id: "reach_contact_sent", label: "Контакты отправлены",
    category: "funnel", seriesColor: "var(--series-4)", format: formatInt,
    description: "Контактная форма отправлена пользователем.",
    formula: "SUM(reaches) WHERE goal_id = contact_sent" },
  // Авто-цели Я.Метрики (могут быть пустыми, но включаем по директиве)
  reach_auto_search: { id: "reach_auto_search", label: "Поиск на сайте",
    category: "funnel", seriesColor: "var(--series-1)", format: formatInt,
    description: "Авто-цель Я.Метрики: пользователь воспользовался поиском.",
    formula: "SUM(reaches) WHERE goal_type = auto_search" },
  reach_auto_social: { id: "reach_auto_social", label: "Клик в соцсеть",
    category: "funnel", seriesColor: "var(--series-1)", format: formatInt,
    description: "Авто-цель: клик по ссылке на соцсеть с сайта.",
    formula: "SUM(reaches) WHERE goal_type = auto_social" },
  reach_auto_form: { id: "reach_auto_form", label: "Отправка формы",
    category: "funnel", seriesColor: "var(--series-4)", format: formatInt,
    description: "Авто-цель: отправлена любая форма на сайте.",
    formula: "SUM(reaches) WHERE goal_type = auto_form" },
  reach_auto_email: { id: "reach_auto_email", label: "Клик на email",
    category: "funnel", seriesColor: "var(--series-1)", format: formatInt,
    description: "Авто-цель: клик по email-ссылке (mailto:).",
    formula: "SUM(reaches) WHERE goal_type = auto_email" },
  reach_auto_messenger: { id: "reach_auto_messenger", label: "Открыть мессенджер",
    category: "funnel", seriesColor: "var(--series-4)", format: formatInt,
    description: "Авто-цель: клик по ссылке в мессенджер (WhatsApp / Telegram).",
    formula: "SUM(reaches) WHERE goal_type = auto_messenger" },

  // === Cost per action — 9 ===
  cpm: { id: "cpm", label: "CPM", category: "money",
    seriesColor: "var(--series-6)", format: formatRub, invertGoodSign: true,
    description: "Стоимость 1000 показов рекламы.",
    formula: "cost_rub / impressions × 1000" },
  cpc: { id: "cpc", label: "CPC", category: "money",
    seriesColor: "var(--series-6)", format: formatRub, invertGoodSign: true,
    description: "Стоимость одного клика по рекламе.",
    formula: "cost_rub / clicks" },
  cpv: { id: "cpv", label: "CPV", category: "money",
    seriesColor: "var(--series-6)", format: formatRub, invertGoodSign: true,
    description: "Стоимость одного визита на сайт (визит ≠ клик из-за bounce).",
    formula: "cost_rub / visits" },
  cp_product_view: { id: "cp_product_view", label: "CP-просмотр товара", category: "money",
    seriesColor: "var(--series-6)", format: formatRub, invertGoodSign: true,
    description: "Стоимость одного просмотра карточки товара.",
    formula: "cost_rub / reach_product_view" },
  cp_product_click: { id: "cp_product_click", label: "CP-клик по товару", category: "money",
    seriesColor: "var(--series-6)", format: formatRub, invertGoodSign: true,
    description: "Стоимость одного клика по товару в листинге.",
    formula: "cost_rub / reach_product_click" },
  cp_basket: { id: "cp_basket", label: "CP-корзина", category: "money",
    seriesColor: "var(--series-6)", format: formatRub, invertGoodSign: true,
    description: "Стоимость одного добавления в корзину (ранний прокси покупки).",
    formula: "cost_rub / reach_basket" },
  cp_checkout: { id: "cp_checkout", label: "CP-чекаут", category: "money",
    seriesColor: "var(--series-6)", format: formatRub, invertGoodSign: true,
    description: "Стоимость одного перехода к оформлению заказа.",
    formula: "cost_rub / reach_checkout" },
  cp_order: { id: "cp_order", label: "CP-заказ (CPA)", category: "money",
    seriesColor: "var(--series-6)", format: formatRub, invertGoodSign: true,
    description: "Стоимость одного оформленного заказа (CPA).",
    formula: "cost_rub / reach_order_placed" },
  cp_buyout: { id: "cp_buyout", label: "CP-выкуп", category: "money",
    seriesColor: "var(--series-6)", format: formatRub, invertGoodSign: true,
    description: "Стоимость одного выкупленного заказа (CPA с поправкой на возвраты).",
    formula: "cost_rub / reach_order_delivered" },

  // === CR между шагами — 5 ===
  cr_click_to_visit: { id: "cr_click_to_visit", label: "CR клик→визит",
    category: "quality", seriesColor: "var(--series-1)",
    format: (n: number | null) => formatPct(n, 1),
    description: "Доля кликов, которые довели пользователя до сайта (отсев = bounce + потери).",
    formula: "visits / clicks × 100%" },
  cr_visit_to_basket: { id: "cr_visit_to_basket", label: "CR визит→корзина",
    category: "quality", seriesColor: "var(--series-3)",
    format: (n: number | null) => formatPct(n, 2),
    description: "Доля визитов с рекламы, добавивших товар в корзину.",
    formula: "reach_basket / visits × 100%" },
  cr_basket_to_checkout: { id: "cr_basket_to_checkout", label: "CR корзина→чекаут",
    category: "quality", seriesColor: "var(--series-4)",
    format: (n: number | null) => formatPct(n, 1),
    description: "Доля корзин, дошедших до начала оформления заказа.",
    formula: "reach_checkout / reach_basket × 100%" },
  cr_checkout_to_order: { id: "cr_checkout_to_order", label: "CR чекаут→заказ",
    category: "quality", seriesColor: "var(--series-5)",
    format: (n: number | null) => formatPct(n, 1),
    description: "Доля начатых оформлений, завершившихся заказом.",
    formula: "reach_order_placed / reach_checkout × 100%" },
  cr_order_to_buyout: { id: "cr_order_to_buyout", label: "% выкупа",
    category: "quality", seriesColor: "var(--series-5)",
    format: (n: number | null) => formatPct(n, 1),
    description: "Доля заказов, которые покупатель забрал и не вернул.",
    formula: "reach_order_delivered / reach_order_placed × 100%" },

  // === Quality — 3 новых ===
  visit_duration: { id: "visit_duration", label: "Длит. визита (сек)",
    category: "quality", seriesColor: "var(--series-2)", format: formatInt,
    description: "Средняя длительность визита в секундах.",
    formula: "AVG(visit_duration_sec)" },
  pages_per_visit: { id: "pages_per_visit", label: "Глубина визита",
    category: "quality", seriesColor: "var(--series-2)",
    format: (n: number | null) => n == null ? "—" : n.toFixed(1),
    description: "Среднее число просмотренных страниц за визит.",
    formula: "SUM(pageviews) / COUNT(visits)" },
  pct_new_users: { id: "pct_new_users", label: "% новых",
    category: "quality", seriesColor: "var(--series-4)",
    format: (n: number | null) => formatPct(n, 0),
    description: "Доля новых пользователей среди визитёров.",
    formula: "new_users / total_users × 100%" },

  // === Cohort / LTV — 4 (Task 21) ===
  ltv_30: { id: "ltv_30", label: "LTV 30 дней", category: "money",
    seriesColor: "var(--series-1)", format: formatRub,
    description: "Средняя выручка с одного покупателя за первые 30 дней.",
    formula: "SUM(revenue WHERE day_offset <= 30) / cohort_size" },
  ltv_90: { id: "ltv_90", label: "LTV 90 дней", category: "money",
    seriesColor: "var(--series-2)", format: formatRub,
    description: "Средняя выручка с одного покупателя за первые 90 дней.",
    formula: "SUM(revenue WHERE day_offset <= 90) / cohort_size" },
  cohort_repeat_rate: { id: "cohort_repeat_rate", label: "% повторных покупок",
    category: "quality", seriesColor: "var(--series-5)",
    format: (n: number | null) => n == null ? "—" : (n * 100).toFixed(1).replace(".", ",") + "%",
    description: "Доля покупателей с 2+ заказами от общего числа покупателей.",
    formula: "repeat_customers / total_customers × 100%" },
  cohort_days_to_2nd: { id: "cohort_days_to_2nd", label: "Дни до 2-й покупки",
    category: "quality", seriesColor: "var(--series-3)",
    format: (n: number | null) => n == null ? "—" : Math.round(n).toString() + " дн",
    invertGoodSign: true,
    description: "Среднее время между 1-й и 2-й покупкой (меньше = лучше).",
    formula: "AVG(date(order_2) - date(order_1))" },
} as const satisfies Record<string, MetricMeta>;

export type MetricId = keyof typeof METRICS;

export const ALL_METRIC_IDS: MetricId[] = Object.keys(METRICS) as MetricId[];

export const MONEY_METRICS: MetricId[] = ALL_METRIC_IDS.filter(
  (id) => METRICS[id].category === "money",
);
export const FUNNEL_METRICS: MetricId[] = ALL_METRIC_IDS.filter(
  (id) => METRICS[id].category === "funnel",
);
export const QUALITY_METRICS: MetricId[] = ALL_METRIC_IDS.filter(
  (id) => METRICS[id].category === "quality",
);
